import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import { Button } from "@material-ui/core";
import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, {
  EditorFieldControl,
  TextFieldControl,
} from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  compose,
  fileSizeValidator,
  imageTypesValidator,
  lengthValidator,
} from "utils/validators";

import { PUBLISH_STATUSES } from "constants/models";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";
import ROUTES from "navigation/routes";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  UploadPreviewFieldControl,
  ImagePreviewContainer,
  PublishStatusWrapper,
} from "./styles";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import ContentTipStore from "../../../../../stores/TipStore";
import TagDictionary from "../../../../../stores/dictionary/TagDictionary";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ tipId: string }, {}, LocationState> &
  SnackbarContextProps & {};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class DefaultTipDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewTip() {
    const { tipId } = this.props.match.params;

    return tipId === undefined;
  }

  get tipId() {
    const { tipId } = this.props.match.params;

    const parsedTipId = Number.parseInt(tipId);
    return typeof parsedTipId === "number" ? parsedTipId : undefined;
  }

  get backClickInfo() {
    return " Back to Tips";
  }

  get backClickPath() {
    return ROUTES.CONTENT_TIPS;
  }

  @computed get displayValues(): any {
    const formData = ContentTipStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get initialValues() {
    if (this.isNewTip) {
      return {};
    } else {
      return {
        ...ContentTipStore.details?.formData,
      };
    }
  }

  @computed get tipPublishStatus() {
    return ContentTipStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    await Promise.all([TagDictionary.fetch()]);

    if (this.isNewTip) {
      history.replace({ state: { editMode: true } });
    } else {
      await ContentTipStore.fetchTipDetails(this.tipId);
    }
  }

  componentWillUnmount() {
    ContentTipStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewTip) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_TIPS_LINK_DETAILS, {
      tipId: this.tipId,
    });
    console.log(path);
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_TIPS_LINK_DETAILS, {
      tipId: this.tipId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  @computed get tagOptions() {
    return [...TagDictionary.options];
  }

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.tipPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.tipPublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const {
      id,
      title,
      body,
      file,
      videoUrl,
      pdfUrl,
      author,
      origin,
      tags,
    } = values;

    const data = {
      title: title?.trim(),
      body: body?.trim(),
      videoUrl: videoUrl?.trim(),
      pdfUrl: pdfUrl?.trim(),
      author: author?.trim(),
      origin: origin?.trim(),
      tagIds: tags?.map(({ id }: { id: number }) => id),
    };

    const payload = { id, data };

    try {
      if (this.isNewTip) {
        const id = await ContentTipStore.createTip(payload.data);

        if (id) {
          if (file) {
            const payloadFile = { file, id };
            await ContentTipStore.editTipIcon(payloadFile);
          }
          const path = generatePath(ROUTES.CONTENT_TIPS_LINK_DETAILS, {
            tipId: id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          ContentTipStore.fetchTipDetails(id);
        }
      } else {
        const { id } = await ContentTipStore.editTipInfo(payload);

        if (id) {
          if (file) {
            if (typeof file !== "string") {
              const payloadFile = { file, id };
              await ContentTipStore.editTipIcon(payloadFile);
            }
          }
        }

        this.exitEditMode();
        ContentTipStore.fetchTipDetails(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Tip Details"
        leftControls={
          this.tipPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.tipPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickInfo}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={ContentTipStore.isTipLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewTip}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{
              dirty: true,
              dirtyFields: true,
              initialValues: true,
              values: true,
            }}
            onChange={debounce(this.onChangeIsPopupDirty, 500)}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column width="50%">
                <Row>
                  {this.isEditMode ? (
                    <UploadPreviewFieldControl
                      variant="outlined"
                      margin="none"
                      name="file"
                      title="Image"
                      acceptedFiles={ACCEPTED_IMAGE_TYPES}
                      validate={compose(
                        this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                        fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                      )}
                    />
                  ) : (
                    <ImagePreviewContainer
                      image={this.displayValues?.imageUrl}
                    />
                  )}
                </Row>
                <Row>
                  {this.isEditMode ? (
                    <UploadPreviewFieldControl
                      variant="outlined"
                      margin="none"
                      name="pdfUrl"
                      title="PDF"
                      acceptedFiles={".pdf"}
                    />
                  ) : (
                    <ImagePreviewContainer image={this.displayValues?.pdfUrl} />
                  )}
                </Row>
              </Column>

              <Column>
                <Row>
                  {this.isEditMode ? (
                    <TextFieldControl
                      variant="outlined"
                      margin="none"
                      name="title"
                      label="Title"
                      validate={lengthValidator(100)}
                      fullWidth
                      required
                    />
                  ) : (
                    <SimpleField
                      label="Title"
                      value={this.displayValues?.title}
                    />
                  )}
                </Row>

                <Row>
                  {this.isEditMode ? (
                    <EditorFieldControl
                      editorRef={this.quillEditorRef}
                      name="body"
                      title="Body"
                      theme="snow"
                      changeDebounce={0}
                    />
                  ) : (
                    <SimpleField
                      label="Body"
                      value={this.displayValues?.body}
                      isRichText
                    />
                  )}
                </Row>

                <Row>
                  {this.isEditMode ? (
                    <TextFieldControl
                      variant="outlined"
                      margin="none"
                      name="videoUrl"
                      label="Video URL"
                      fullWidth
                    />
                  ) : (
                    <SimpleField
                      label="Video URL"
                      value={this.displayValues?.videoUrl}
                    />
                  )}
                </Row>

                <Row>
                  {this.isEditMode ? (
                    <TextFieldControl
                      variant="outlined"
                      margin="none"
                      name="author"
                      label="Author"
                      fullWidth
                    />
                  ) : (
                    <SimpleField
                      label="Author"
                      value={this.displayValues?.author}
                    />
                  )}
                </Row>

                <Row>
                  {this.isEditMode ? (
                    <TextFieldControl
                      variant="outlined"
                      margin="none"
                      name="origin"
                      label="Origin"
                      fullWidth
                    />
                  ) : (
                    <SimpleField
                      label="Origin"
                      value={this.displayValues?.origin}
                    />
                  )}
                </Row>

                <Row>
                  {this.isEditMode ? (
                    <DropdownFieldControl
                      name="tags"
                      options={this.tagOptions}
                      label="Tags"
                      multi
                      isClearable
                    />
                  ) : (
                    <SimpleField
                      label="Tags"
                      value={this.displayValues?.tags}
                    />
                  )}
                </Row>
              </Column>
            </GroupRow>
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={ContentTipStore.isTipManaging}
      fullWidth
    >
      {this.isNewTip ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(DefaultTipDetails));
